import { LeasedTimer } from '../../../util/promise';
import {DefaultSettings, settings} from "../../../settings/settings-util";
import {Store} from "redux";
import {AlmeState} from "../../../wireup/AlmeState";
import LiveChatRetriever from "./livechat-retriever";
import LiveChatState from "./livechat-state";


export default class LiveChatPoller
{
    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _retriever: LiveChatRetriever;
    _state: LiveChatState;
    _pollConfig: typeof settings.livechat.pollInterval;
    _pollInterval: number;
    _timer: LeasedTimer;


    //#region Public Interface

    constructor(passedSettings: DefaultSettings, retriever: LiveChatRetriever, state: LiveChatState)
    {
        this._settings = passedSettings;
        this._retriever = retriever;
        this._state = state;

        this._pollConfig = passedSettings.livechat.pollInterval;
        this._pollInterval = this._pollConfig.standby.startMs;

        this._timer = new LeasedTimer();
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;

        // Restart polling when coming online
        this._state.addStatusChangeHandler((fresh: string, old: string) =>
        {
            if (this._state.isStarting(fresh, old))
            {
                this.poll(true);
            }
        });
    }

    getPromise(): Promise<void>
    {
        this.poll(true);

        return Promise.resolve();
    }

    //#endregion



    //#region Helper Methods

    nextInterval(shouldReset: boolean): number
    {
        const status = this._state.isOnline ? 'active' : 'standby'; 
        const statusConfig = this._pollConfig[status];

        if (shouldReset)
        {
            this.resetInterval(statusConfig.startMs);
        }
        else if (this._pollInterval < statusConfig.maxMs)
        {
            const { backoffSequence, difference, ratio, maxMs } = statusConfig;

            this.recalculateInterval(backoffSequence, difference, ratio, maxMs);
        }

        return this._pollInterval;
    }

    resetInterval(intervalMs: number): void
    {
        this._pollInterval = intervalMs;
    }

    recalculateInterval(backoffSequence: string, difference: number, ratio: number, maxMs: number): void
    {
        if (backoffSequence === 'linear')
        {
            this._pollInterval += difference;
        }
        else if (backoffSequence === 'exponential')
        {
            this._pollInterval *= ratio;
        }
        else
        {
            throw new Error('Missing backoff sequence config for poller.');
        }

        if (this._pollInterval > maxMs)
        {
            this._pollInterval = maxMs;
        }
    }

    /**
     * Kick off polling, retrieving messages and waiting prescribed delay
     * before getting more.
     * @param {boolean} shouldReset "Heat up" - set interval to min/start
     */
    async poll(shouldReset = false): Promise<void>
    {
        const intervalMs = this.nextInterval(shouldReset);

        if (!intervalMs) { return; }

        const lease = this._timer.lease(intervalMs);

        if (!lease.success) { return; }

        const shouldPoll = !this._state.isOfflineStatus(this._state.sessionStatus);

        if(!shouldPoll) { 
            await lease.done;
            // Avoid recursion
            setTimeout((): void => { this.poll(false); }, 0);

            return;
        }

        const retrievedMessages = await this._retriever.asyncRetrieveMessages();
            
        await lease.done;

        const shouldResetNext = retrievedMessages.length > 0;

        // Avoid recursion
        setTimeout((): void => { this.poll(shouldResetNext); }, 0);
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/livechat-foundation/src/livechat-poller.ts